/* global atlas */
import {
  buildPlanMapRouteTooltipHtml,
  buildPlanMapStopTooltipHtml,
} from "../plans/components/tabs/components/planMapTooltip";
import {
  MAP_BASE_STYLE,
  MAP_BASE_STYLE_OVERRIDES,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  clampZoom,
} from "./map-controller-utils";
import {
  buildArrowDataUri,
  buildRouteWaypoints,
  getStoredReturnGeometry,
  getStoredRouteGeometry,
  getCoordinates,
  getOrderedRouteStops,
  lightenHexColor,
  sanitizeHexColor,
  stableStringify,
} from "./route-geometry";
import {
  escapeHtml,
  formatAddressLabel,
  formatAlertsText,
  formatSecondsAsMinutes,
  normalizeAlerts,
} from "../../utils/formatters";
export function createPlanMapController(options) {
  const runtimeOptions = options && typeof options === 'object' ? options : {};
  const rootElement = runtimeOptions.rootElement || document;
  const mapElement = runtimeOptions.mapElement || (rootElement && rootElement.querySelector
    ? rootElement.querySelector('#map')
    : document.getElementById('map'));
  const tooltipHostElement = (mapElement && mapElement.parentElement) || mapElement || rootElement;
  const initialSubscriptionKey = String(runtimeOptions.initialSubscriptionKey || '').trim();
  const initialStyle = String(runtimeOptions.initialStyle || MAP_BASE_STYLE).trim() || MAP_BASE_STYLE;
  const sampleContract = runtimeOptions.sampleContract || null;
  const eventBindings = [];
  const DEFAULT_ZOOM = 12;
  const DEFAULT_PADDING = 64;
  const DELIVERY_LINE_FILTER = ['all', ['==', ['geometry-type'], 'LineString'], ['!=', ['get', 'isReturn'], true]];
  const RETURN_LINE_FILTER = ['==', ['get', 'isReturn'], true];
  const LEGACY_CONTRACT_STORAGE_KEY = 'ttmm.azure_web_poc.contract';
  const STORAGE_KEYS = {
    subscriptionKey: 'ttmm.azure_web_poc.subscription_key',
    settings: 'ttmm.azure_web_poc.settings'
  };

  const getById = function (id) {
    if (rootElement && typeof rootElement.querySelector === 'function') {
      const fromRoot = rootElement.querySelector(`#${id}`);
      if (fromRoot) {
        return fromRoot;
      }
    }
    return document.getElementById(id);
  };

  const ui = {
    subscriptionKey: getById('subscriptionKey'),
    showTraffic: getById('showTraffic'),
    autoFit: getById('autoFit'),
    contractInput: getById('contractInput'),
    loadSample: getById('loadSample'),
    formatJson: getById('formatJson'),
    clearMap: getById('clearMap'),
    renderMap: getById('renderMap'),
    fitMap: getById('fitMap'),
    zoomInBtn: getById('zoomInBtn'),
    resetViewBtn: getById('resetViewBtn'),
    zoomOutBtn: getById('zoomOutBtn'),
    trafficToggleBtn: getById('trafficToggleBtn'),
    showAllRoutes: getById('showAllRoutes'),
    hideAllRoutes: getById('hideAllRoutes'),
    routeList: getById('routeList'),
    statusBox: getById('statusBox'),
    statusText: getById('statusText'),
    cacheBadge: getById('cacheBadge'),
    routesCount: getById('routesCount'),
    stopsCount: getById('stopsCount')
  };

  function bindDomEvent(target, eventName, handler) {
    if (!target || typeof target.addEventListener !== 'function') {
      return;
    }
    target.addEventListener(eventName, handler);
    eventBindings.push({ target, eventName, handler });
  }

  const state = {
    map: null,
    mapReady: null,
    activeSubscriptionKey: '',
    activeMapConfigHash: '',
    routeSource: null,
    boundsSource: null,
    hoverTooltip: null,
    markers: [],
    arrowIconColors: new Set(),
    renderedRouteGroups: [],
    activeRouteIds: new Set(),
    lastContractHash: '',
    resizeObserver: null,
    resizeTimer: null,
    initialRenderStarted: false,
    lastRenderFitMaxZoom: null,
    initialViewSnapshot: null
  };

  function init() {
    if (!window.atlas) {
      throw new Error('Azure Maps SDK no esta cargado en window.atlas');
    }
    if (!mapElement) {
      throw new Error('No se encontro el contenedor #map dentro del componente.');
    }
    if (!ui.subscriptionKey || !ui.contractInput || !ui.statusBox || !ui.statusText) {
      throw new Error('Faltan elementos de UI requeridos para inicializar el mapa.');
    }

    hydrateFromStorage();
    bindEvents();
    ensureMap();
    ensureSampleLoaded();
    attemptInitialRender();
  }

  function hydrateFromStorage() {
    localStorage.removeItem(LEGACY_CONTRACT_STORAGE_KEY);

    const configuredKey = (
      initialSubscriptionKey ||
      (window.__TTMM_AZURE_WEB_POC__ &&
      window.__TTMM_AZURE_WEB_POC__.subscriptionKey)
    ) || '';
    ui.subscriptionKey.value = configuredKey || localStorage.getItem(STORAGE_KEYS.subscriptionKey) || '';

    const rawSettings = localStorage.getItem(STORAGE_KEYS.settings);
    if (rawSettings) {
      try {
        const settings = JSON.parse(rawSettings);
        ui.showTraffic.checked = settings.showTraffic === true;
        ui.autoFit.checked = settings.autoFit !== false;
      } catch (_error) {
        // Ignorar settings corrupto y continuar con defaults.
      }
    }

  }

  function persistState() {
    localStorage.setItem(STORAGE_KEYS.subscriptionKey, ui.subscriptionKey.value.trim());
    localStorage.setItem(
      STORAGE_KEYS.settings,
      JSON.stringify({
        showTraffic: ui.showTraffic.checked,
        autoFit: ui.autoFit.checked
      })
    );
  }

  function bindEvents() {
    bindDomEvent(ui.loadSample, 'click', ensureSampleLoaded);
    bindDomEvent(ui.formatJson, 'click', formatContractInput);
    bindDomEvent(ui.clearMap, 'click', clearMap);
    bindDomEvent(ui.renderMap, 'click', renderFromInput);
    bindDomEvent(ui.fitMap, 'click', resetMapViewToInitialFit);
    bindDomEvent(ui.zoomInBtn, 'click', function () { stepZoom(1); });
    bindDomEvent(ui.resetViewBtn, 'click', resetMapViewToInitialFit);
    bindDomEvent(ui.zoomOutBtn, 'click', function () { stepZoom(-1); });
    bindDomEvent(ui.trafficToggleBtn, 'click', function () {
      ui.showTraffic.checked = !ui.showTraffic.checked;
      persistState();
      syncTraffic();
    });
    bindDomEvent(ui.showAllRoutes, 'click', function () { setAllRoutesVisible(true); });
    bindDomEvent(ui.hideAllRoutes, 'click', function () { setAllRoutesVisible(false); });

    bindDomEvent(ui.subscriptionKey, 'change', persistState);
    bindDomEvent(ui.showTraffic, 'change', async function () {
      persistState();
      await ensureMap();
      syncTraffic();
    });
    bindDomEvent(ui.autoFit, 'change', persistState);
    bindDomEvent(ui.contractInput, 'input', persistState);
  }

  function ensureMap() {
    const subscriptionKey = ui.subscriptionKey.value.trim();
    const mapConfigHash = stableStringify({ subscriptionKey });
    if (!subscriptionKey) {
      setStatus(
        'Falta la key',
        'No se ha encontrado la Azure Maps key. Revisa BEEPAKET_AZURE_SUBSCRIPTION_KEY en local.settings.json o pegala manualmente.',
        'error'
      );
      return Promise.resolve(null);
    }

    if (
      state.map &&
      state.activeSubscriptionKey === subscriptionKey &&
      state.activeMapConfigHash === mapConfigHash
    ) {
      return state.mapReady;
    }

    destroyMap();
    state.activeSubscriptionKey = subscriptionKey;
    state.activeMapConfigHash = mapConfigHash;
    state.map = new atlas.Map(mapElement, {
      view: 'Auto',
      style: initialStyle,
      // Configuracion fija de la PoC: no se expone en la UI ni en el contrato.
      styleOverrides: MAP_BASE_STYLE_OVERRIDES,
      zoom: DEFAULT_ZOOM,
      minZoom: MAP_MIN_ZOOM,
      maxZoom: MAP_MAX_ZOOM,
      center: [-3.7038, 40.4168],
      authOptions: {
        authType: 'subscriptionKey',
        subscriptionKey
      }
    });

    state.mapReady = new Promise(function (resolve) {
      state.map.events.add('ready', function () {
        state.routeSource = new atlas.source.DataSource();
        state.boundsSource = new atlas.source.DataSource();

        state.map.sources.add(state.routeSource);
        state.map.sources.add(state.boundsSource);

        state.map.layers.add(new atlas.layer.LineLayer(state.routeSource, 'route-lines-glow', {
          strokeColor: ['get', 'glowColor'],
          strokeWidth: 9,
          strokeOpacity: 0.18,
          blur: 1.2,
          filter: DELIVERY_LINE_FILTER
        }));

        state.map.layers.add(new atlas.layer.LineLayer(state.routeSource, 'route-lines', {
          strokeColor: ['get', 'strokeColor'],
          strokeWidth: 4,
          strokeOpacity: 0.92,
          lineJoin: 'round',
          lineCap: 'round',
          filter: DELIVERY_LINE_FILTER
        }));

        state.map.layers.add(new atlas.layer.LineLayer(state.routeSource, 'route-return-lines', {
          strokeColor: ['get', 'strokeColor'],
          strokeWidth: 2.5,
          strokeOpacity: 0.8,
          strokeDashArray: [2, 2],
          lineJoin: 'round',
          filter: RETURN_LINE_FILTER
        }));

        state.map.layers.add(new atlas.layer.SymbolLayer(state.routeSource, 'route-arrows', {
          placement: 'line',
          lineSpacing: 120,
          iconOptions: {
            image: ['concat', 'route-arrow-', ['get', 'routeColorHex']],
            allowOverlap: true,
            ignorePlacement: true,
            rotationAlignment: 'map',
            anchor: 'center',
            size: 1.0,
            opacity: 0.9
          },
          filter: DELIVERY_LINE_FILTER
        }));

        syncTraffic();
        observeMapResize();
        state.map.events.add('zoomend', updateZoomButtonsState);
        resolve();
      });
    });

    return state.mapReady;
  }

  function destroyMap() {
    if (state.resizeObserver) {
      state.resizeObserver.disconnect();
      state.resizeObserver = null;
    }
    if (state.hoverTooltip) {
      state.hoverTooltip.remove();
      state.hoverTooltip = null;
    }
    if (state.map) {
      state.map.dispose();
    }
    state.map = null;
    state.mapReady = null;
    state.activeMapConfigHash = '';
    state.routeSource = null;
    state.boundsSource = null;
    state.markers = [];
    state.arrowIconColors = new Set();
    state.initialViewSnapshot = null;
    state.lastRenderFitMaxZoom = null;
  }

  function observeMapResize() {
    if (!window.ResizeObserver || !mapElement) {
      return;
    }

    state.resizeObserver = new ResizeObserver(function () {
      clearTimeout(state.resizeTimer);
      state.resizeTimer = setTimeout(function () {
        if (!state.map) {
          return;
        }
        state.map.resize();
        if (ui.autoFit.checked && state.boundsSource && state.boundsSource.getShapes().length > 0) {
          fitToRenderedData(false, state.lastRenderFitMaxZoom);
        }
      }, 180);
    });
    state.resizeObserver.observe(mapElement);
  }

  async function ensureSampleLoaded() {
    if (ui.contractInput.value.trim()) {
      return;
    }

    if (sampleContract && typeof sampleContract === 'object') {
      ui.contractInput.value = JSON.stringify(sampleContract, null, 2);
      persistState();
      setStatus('Sample cargado', 'Puedes renderizarlo tal cual o editar el JSON.', 'ok');
      await attemptInitialRender();
      return;
    }

    setStatus('Sample no disponible', 'No hay contrato de ejemplo configurado en la vista.', 'error');
  }

  async function attemptInitialRender() {
    if (state.initialRenderStarted || !ui.subscriptionKey.value.trim() || !ui.contractInput.value.trim()) {
      return;
    }

    try {
      state.initialRenderStarted = true;
      const contract = JSON.parse(ui.contractInput.value);
      await renderContract(contract);
    } catch (_error) {
      // El render inicial es best-effort; la UI queda lista para render manual.
    }
  }

  function formatContractInput() {
    try {
      const parsed = JSON.parse(ui.contractInput.value || '{}');
      ui.contractInput.value = JSON.stringify(parsed, null, 2);
      persistState();
      setStatus('JSON formateado', 'El contrato se ha normalizado para facilitar la edicion.', 'ok');
    } catch (error) {
      setStatus('JSON no valido', error.message, 'error');
    }
  }

  async function renderFromInput() {
    try {
      const contract = JSON.parse(ui.contractInput.value || '{}');
      persistState();
      await renderContract(contract);
    } catch (error) {
      setStatus('No se pudo renderizar', error.message, 'error');
    }
  }

  async function renderContract(contract) {
    if (!ui.subscriptionKey.value.trim()) {
      throw new Error(
        'La subscription key de Azure Maps es obligatoria para cargar el mapa. Revisa BEEPAKET_AZURE_SUBSCRIPTION_KEY en local.settings.json.'
      );
    }

    await ensureMap();

    const normalizedContract = normalizeContract(contract);
    const contractHash = stableStringify(normalizedContract);
    state.lastRenderFitMaxZoom = normalizedContract.zoom;

    syncTraffic(normalizedContract.show_traffic);

    if (contractHash === state.lastContractHash) {
      if (ui.autoFit.checked) {
        fitToRenderedData(false, normalizedContract.zoom, true);
      }
      setStatus(
        'Contrato sin cambios',
        'Se han reutilizado las geometrias cacheadas; no se ha vuelto a consultar Azure Maps.',
        'ok'
      );
      updateStats(getVisibleRouteGroups().length, countVisibleStops(getVisibleRouteGroups()));
      return;
    }

    const renderModel = await buildRenderModel(normalizedContract);
    drawRenderModel(renderModel);

    state.lastContractHash = contractHash;

    if (ui.autoFit.checked) {
      fitToRenderedData(false, normalizedContract.zoom, true);
    }
    setStatus(
      'Mapa renderizado',
      'Las geometrias quedan en cliente y el zoom posterior no vuelve a pedir rutas.',
      'ok'
    );
  }

  function normalizeContract(contract) {
    const normalized = contract && typeof contract === 'object' ? contract : {};
    const zoom = normalized.zoom != null ? clampZoom(normalized.zoom) : DEFAULT_ZOOM;

    return {
      zoom,
      width: toPositiveInt(normalized.width),
      height: toPositiveInt(normalized.height),
      show_traffic: normalized.show_traffic != null ? Boolean(normalized.show_traffic) : ui.showTraffic.checked,
      routes: Array.isArray(normalized.routes) ? normalized.routes : []
    };
  }

  async function buildRenderModel(contract) {
    const routeGroups = [];

    for (const route of contract.routes) {
      const routeModel = await buildRouteGroup(route);
      if (!routeModel) {
        continue;
      }

      routeGroups.push(routeModel);
    }

    return {
      routeGroups
    };
  }

  async function buildRouteGroup(route) {
    const waypoints = buildRouteWaypoints(route);
    if (waypoints.length < 2) {
      return null;
    }

    const routeColor = sanitizeHexColor(route.route_color_hex);
    const stopColor = lightenHexColor(routeColor, 0.18);
    const pathPoints = getStoredRouteGeometry(route) || [];
    const returnPoints = getStoredReturnGeometry(route) || [];

    const orderedStops = getOrderedRouteStops(route);
    const routableStops = orderedStops.filter(function (stop) {
      return Boolean(getCoordinates(stop.address_normalized));
    });

    return {
      routeId: String(route._id || route.id || route.vehicle_id || route.name || route.abbreviation || stableStringify(waypoints)),
      routeName: String(route.name || route.abbreviation || 'Ruta'),
      routeColor: `#${routeColor}`,
      glowColor: `#${lightenHexColor(routeColor, 0.32)}`,
      stopColor: `#${stopColor}`,
      routeDistance: `${String(route.km ?? route.distance ?? '').trim() || '0'} km`,
      routeTimeWindow: formatRouteTimeWindowLabel(route.time_window),
      routeTotalTime: formatRouteTotalTimeLabel(route.total_time),
      stopCount: String(routableStops.length),
      warningText: formatAlertsText(route.alerts),
      alerts: normalizeAlerts(route.alerts),
      origin: {
        position: waypoints[0],
        label: String(route.abbreviation || route.name || 'O').slice(0, 2)
      },
      destination: buildRouteDestination(route, waypoints[0]),
      stops: routableStops.map(function (stop, index) {
        return {
          id: stop.route_stop_id || stop.stop_id || `${route.name || 'route'}-${index + 1}`,
          position: getCoordinates(stop.address_normalized),
          label: String(stop.order || index + 1),
          number: String(stop.number || ''),
          alerts: normalizeAlerts(stop.alerts),
          status: String(stop.status || '').trim() || 'Pendiente',
          contact: getStopContact(stop),
          address: getStopAddressLabel(stop),
          timeWindow1: normalizeTimeWindowLabel(stop.time_window_1 || stop.time_window),
          timeWindow2: normalizeTimeWindowLabel(stop.time_window_2)
        };
      }),
      pathPoints,
      returnPoints,
      boundsPoints: pathPoints.length > 1 ? pathPoints.concat(returnPoints) : waypoints
    };
  }

  function buildRouteDestination(route, origin) {
    const position = getCoordinates(route.to_location);
    if (!position || !origin || (position[0] === origin[0] && position[1] === origin[1])) {
      return null;
    }

    return {
      position,
      label: String(route.abbreviation || route.name || 'F').slice(0, 2)
    };
  }

  function normalizeTimeWindowLabel(value) {
    if (!value) {
      return '-';
    }

    if (typeof value === 'string') {
      const text = String(value).trim();
      return text || '-';
    }

    if (typeof value === 'object') {
      const start = String(value.start_time || value.start || '').trim();
      const end = String(value.end_time || value.end || '').trim();
      if (start && end) {
        return `${start} - ${end}`;
      }
      return start || end || '-';
    }

    return '-';
  }

  function getStopContact(stop) {
    return stop.contact.name;
  }

  function getStopAddressLabel(stop) {
    if (stop && stop.address_normalized) {
      const combined = formatAddressLabel(stop.address_normalized, '-');
      if (combined) return combined;
    }

    if (typeof stop.address === 'string') {
      return String(stop.address).trim() || '-';
    }

    if (stop && stop.address && typeof stop.address === 'object') {
      return String(stop.address.text || stop.address.address || stop.address.street || '').trim() || '-';
    }

    return '-';
  }

  function formatRouteTimeWindowLabel(value) {
    if (typeof value === 'string') {
      return String(value).trim() || '-';
    }

    if (!value || typeof value !== 'object') {
      return '-';
    }

    const start = String(value.start_time || value.start || '').trim();
    const end = String(value.end_time || value.end || '').trim();
    if (start && end) {
      return `${start}-${end}`;
    }

    return start || end || '-';
  }

  function formatRouteTotalTimeLabel(value) {
    return formatSecondsAsMinutes(value, '0 min');
  }

  function buildOriginPopupHtml(group) {
    return buildPlanMapRouteTooltipHtml(group);
  }

  function buildStopPopupHtml(group, stop) {
    return buildPlanMapStopTooltipHtml(group, stop);
  }

  function ensureHoverTooltip() {
    if (state.hoverTooltip) {
      return state.hoverTooltip;
    }

    if (!tooltipHostElement || typeof tooltipHostElement.appendChild !== 'function') {
      return null;
    }

    const tooltip = document.createElement('div');
    tooltip.className = 'map-stop-tooltip';
    tooltip.style.display = 'none';
    tooltipHostElement.appendChild(tooltip);
    state.hoverTooltip = tooltip;
    return tooltip;
  }

  function positionTooltipNearAnchor(tooltip, anchorRect, rootRect) {
    const GAP = 10;
    const PADDING = 8;

    tooltip.style.transform = 'none';
    tooltip.style.left = '-9999px';
    tooltip.style.top = '-9999px';

    const tooltipW = tooltip.offsetWidth;
    const tooltipH = tooltip.offsetHeight;

    const anchorCenterX = anchorRect.left - rootRect.left + anchorRect.width / 2;
    const anchorTopY = anchorRect.top - rootRect.top;

    let left = anchorCenterX - tooltipW / 2;
    let top = anchorTopY - tooltipH - GAP;

    left = Math.max(PADDING, Math.min(left, rootRect.width - tooltipW - PADDING));

    if (top < PADDING) {
      top = anchorTopY + anchorRect.height + GAP;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }

  function showStopTooltip(anchorElement, group, stop) {
    const tooltip = ensureHoverTooltip();
    if (
      !tooltip ||
      !anchorElement ||
      !tooltipHostElement ||
      typeof tooltipHostElement.getBoundingClientRect !== 'function'
    ) {
      return;
    }

    const rootRect = tooltipHostElement.getBoundingClientRect();
    const anchorRect = anchorElement.getBoundingClientRect();
    tooltip.innerHTML = buildStopPopupHtml(group, stop);
    tooltip.style.display = 'block';
    positionTooltipNearAnchor(tooltip, anchorRect, rootRect);
  }

  function hideStopTooltip() {
    if (!state.hoverTooltip) {
      return;
    }

    state.hoverTooltip.style.display = 'none';
  }

  function drawRenderModel(renderModel) {
    state.renderedRouteGroups = renderModel.routeGroups.slice();
    state.activeRouteIds = new Set(
      renderModel.routeGroups.map(function (group) { return group.routeId; })
    );
    renderRouteVisibilityControls();
    redrawVisibleRoutes();
    updateCacheBadge(renderModel.routeGroups);
  }

  function redrawVisibleRoutes() {
    clearMarkers();
    state.routeSource.clear();
    state.boundsSource.clear();

    const visibleRouteGroups = getVisibleRouteGroups();
    const features = [];
    const boundsFeatures = [];

    for (const group of visibleRouteGroups) {
      const colorHex = group.routeColor.replace('#', '');
      if (!state.arrowIconColors.has(colorHex)) {
        state.arrowIconColors.add(colorHex);
        state.map.imageSprite.add('route-arrow-' + colorHex, buildArrowDataUri(group.routeColor));
      }
      if (group.pathPoints.length > 1) {
        features.push(
          new atlas.data.Feature(
            new atlas.data.LineString(group.pathPoints),
            {
              routeId: group.routeId,
              routeName: group.routeName,
              strokeColor: group.routeColor,
              routeColorHex: colorHex,
              glowColor: group.glowColor
            }
          )
        );
      }
      if (group.returnPoints.length > 1) {
        features.push(
          new atlas.data.Feature(
            new atlas.data.LineString(group.returnPoints),
            {
              routeId: group.routeId,
              routeName: group.routeName,
              strokeColor: group.routeColor,
              isReturn: true
            }
          )
        );
      }

      boundsFeatures.push(
        new atlas.data.Feature(
          new atlas.data.LineString(group.boundsPoints),
          { kind: 'bounds', routeId: group.routeId }
        )
      );

      state.markers.push(createRouteEndpointMarker(group, group.origin, false));
      if (group.destination) {
        state.markers.push(createRouteEndpointMarker(group, group.destination, true));
      }
      for (const stop of group.stops) {
        state.markers.push(createStopMarker(group, stop));
      }
    }

    if (features.length > 0) {
      state.routeSource.add(features);
    }
    if (boundsFeatures.length > 0) {
      state.boundsSource.add(boundsFeatures);
    }

    updateStats(visibleRouteGroups.length, countVisibleStops(visibleRouteGroups));
  }

  function getVisibleRouteGroups() {
    return state.renderedRouteGroups.filter(function (group) {
      return state.activeRouteIds.has(group.routeId);
    });
  }

  function countVisibleStops(routeGroups) {
    return routeGroups.reduce(function (total, group) {
      return total + group.stops.length;
    }, 0);
  }

  function renderRouteVisibilityControls() {
    if (!state.renderedRouteGroups.length) {
      ui.routeList.innerHTML = '<p class="contract-help">Renderiza el mapa para gestionar la visibilidad por ruta.</p>';
      return;
    }

    ui.routeList.innerHTML = state.renderedRouteGroups.map(function (group) {
      const checked = state.activeRouteIds.has(group.routeId) ? 'checked' : '';
      return `
        <div class="route-toggle">
          <label>
            <input type="checkbox" data-route-id="${escapeHtml(group.routeId)}" ${checked}>
            <span class="swatch" style="background:${group.routeColor};"></span>
            <span>${escapeHtml(group.routeName)}</span>
          </label>
          <small>${group.stops.length} paradas</small>
        </div>
      `;
    }).join('');

    Array.from(ui.routeList.querySelectorAll('input[type="checkbox"]')).forEach(function (checkbox) {
      checkbox.addEventListener('change', function () {
        const routeId = checkbox.dataset.routeId || '';
        if (checkbox.checked) {
          state.activeRouteIds.add(routeId);
        } else {
          state.activeRouteIds.delete(routeId);
        }
        redrawVisibleRoutes();
        if (ui.autoFit.checked) {
          fitToRenderedData(false, state.lastRenderFitMaxZoom);
        }
      });
    });
  }

  function setAllRoutesVisible(visible) {
    if (!state.renderedRouteGroups.length) {
      return;
    }

    state.activeRouteIds = visible
      ? new Set(state.renderedRouteGroups.map(function (group) { return group.routeId; }))
      : new Set();

    renderRouteVisibilityControls();
    redrawVisibleRoutes();
    if (visible && ui.autoFit.checked) {
      fitToRenderedData(false, state.lastRenderFitMaxZoom);
    }
  }

  function setVisibleRouteIds(routeIds) {
    if (!state.renderedRouteGroups.length) {
      return;
    }

    const normalizedIds = Array.isArray(routeIds)
      ? routeIds.map(function (id) { return String(id || '').trim(); }).filter(Boolean)
      : [];
    const allowedRouteIds = new Set(normalizedIds);

    if (allowedRouteIds.size === 0) {
      state.activeRouteIds = new Set();
    } else {
      state.activeRouteIds = new Set(
        state.renderedRouteGroups
          .map(function (group) { return group.routeId; })
          .filter(function (routeId) { return allowedRouteIds.has(routeId); })
      );
    }

    renderRouteVisibilityControls();
    redrawVisibleRoutes();
    if (ui.autoFit.checked) {
      fitToRenderedData(false, state.lastRenderFitMaxZoom);
    }
  }

  function createRouteEndpointMarker(group, endpoint, isDestination) {
    const markerElement = document.createElement('div');
    markerElement.className = 'marker-origin';
    if (Array.isArray(group.alerts) && group.alerts.length > 0) {
      markerElement.classList.add('has-warning');
    }
    if (isDestination) {
      markerElement.classList.add('marker-destination');
      markerElement.title = `${group.routeName} · fin de ruta`;
      markerElement.style.background = '#fff';
      markerElement.style.color = group.routeColor;
    } else {
      markerElement.title = group.routeName;
      markerElement.style.background = group.routeColor;
    }
    markerElement.innerHTML = `<span class="marker-origin-label">${escapeHtml(endpoint.label)}</span>`;

    markerElement.addEventListener('mouseenter', function () {
      const tooltip = ensureHoverTooltip();
      if (
        !tooltip ||
        !tooltipHostElement ||
        typeof tooltipHostElement.getBoundingClientRect !== 'function'
      ) {
        return;
      }

      const rootRect = tooltipHostElement.getBoundingClientRect();
      const anchorRect = markerElement.getBoundingClientRect();
      tooltip.innerHTML = buildOriginPopupHtml(group);
      tooltip.style.display = 'block';
      positionTooltipNearAnchor(tooltip, anchorRect, rootRect);
    });

    markerElement.addEventListener('mouseleave', function () {
      hideStopTooltip();
    });

    const marker = new atlas.HtmlMarker({
      position: endpoint.position,
      htmlContent: markerElement
    });
    state.map.markers.add(marker);
    return marker;
  }

  function createStopMarker(group, stop) {
    const title = stop.number ? `${group.routeName} · parada ${stop.number}` : group.routeName;
    const markerElement = document.createElement('div');
    markerElement.className = 'marker-stop';
    if (Array.isArray(stop.alerts) && stop.alerts.length > 0) {
      markerElement.classList.add('has-warning');
    }
    markerElement.title = title;
    markerElement.style.background = group.stopColor;
    markerElement.textContent = stop.label;

    markerElement.addEventListener('mouseenter', function () {
      showStopTooltip(markerElement, group, stop);
    });

    markerElement.addEventListener('mouseleave', function () {
      hideStopTooltip();
    });

    const marker = new atlas.HtmlMarker({
      position: stop.position,
      htmlContent: markerElement
    });
    state.map.markers.add(marker);
    return marker;
  }

  function fitToRenderedData(force, maxZoom, rememberAsInitialView) {
    if (!state.map || !state.boundsSource) {
      return;
    }

    const shapes = state.boundsSource.getShapes();
    if (!shapes.length) {
      if (force) {
        setStatus('Sin geometrias', 'No hay datos renderizados para recentrar la camara.', 'error');
      }
      return;
    }

    const positions = [];
    for (const shape of shapes) {
      const currentPositions = getShapePositions(shape);
      for (const position of currentPositions) {
        positions.push(position);
      }
    }

    if (!positions.length) {
      return;
    }

    const bounds = atlas.data.BoundingBox.fromPositions(positions);
    const duration = force ? 700 : 0;
    state.map.setCamera({
      bounds,
      padding: getPadding(),
      maxZoom: maxZoom != null ? clampZoom(maxZoom) : MAP_MAX_ZOOM,
      duration,
      type: force ? 'fly' : 'ease'
    });
    if (rememberAsInitialView) {
      rememberInitialViewSnapshot(positions, maxZoom);
    }
  }

  function resetMapViewToInitialFit() {
    if (!state.map || !state.initialViewSnapshot) {
      fitToRenderedData(true, state.lastRenderFitMaxZoom);
      return;
    }

    state.activeRouteIds = new Set(state.initialViewSnapshot.routeIds);
    renderRouteVisibilityControls();
    redrawVisibleRoutes();
    state.map.setCamera({
      bounds: atlas.data.BoundingBox.fromPositions(state.initialViewSnapshot.positions),
      padding: DEFAULT_PADDING,
      maxZoom: state.initialViewSnapshot.fitMaxZoom,
      duration: 700,
      type: 'fly'
    });
  }

  function syncTraffic(forcedValue) {
    if (!state.map) {
      return;
    }

    const enabled = forcedValue != null ? Boolean(forcedValue) : ui.showTraffic.checked;
    state.map.setTraffic({
      incidents: enabled,
      flow: enabled ? 'relative' : 'none'
    });
    ui.trafficToggleBtn.classList.toggle('is-active', enabled);
    ui.trafficToggleBtn.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    ui.trafficToggleBtn.classList.remove('is-active-pulse');
    if (enabled) {
      void ui.trafficToggleBtn.offsetWidth;
      ui.trafficToggleBtn.classList.add('is-active-pulse');
    }
  }

  function setTrafficEnabled(value) {
    const enabled = Boolean(value);
    if (ui.showTraffic) {
      ui.showTraffic.checked = enabled;
    }
    persistState();
    syncTraffic(enabled);
  }

  function stepZoom(delta) {
    if (!state.map) {
      return;
    }

    const camera = state.map.getCamera();
    const targetZoom = clampZoom((camera.zoom || DEFAULT_ZOOM) + delta);
    state.map.setCamera({
      center: camera.center,
      zoom: targetZoom,
      duration: 180,
      type: 'ease'
    });
    updateZoomButtonsState();
  }

  function updateZoomButtonsState() {
    if (!state.map) {
      return;
    }

    const camera = state.map.getCamera();
    const currentZoom = clampZoom(camera.zoom);
    if (ui.zoomInBtn) ui.zoomInBtn.disabled = currentZoom >= MAP_MAX_ZOOM;
    if (ui.zoomOutBtn) ui.zoomOutBtn.disabled = currentZoom <= MAP_MIN_ZOOM;
  }

  function clearMap() {
    if (!state.map) {
      setStatus('Mapa vacio', 'Todavia no hay una instancia de mapa creada.', 'error');
      return;
    }
    if (state.routeSource) {
      state.routeSource.clear();
    }
    if (state.boundsSource) {
      state.boundsSource.clear();
    }
    clearMarkers();
    state.renderedRouteGroups = [];
    state.activeRouteIds = new Set();
    state.initialViewSnapshot = null;
    state.lastRenderFitMaxZoom = null;
    renderRouteVisibilityControls();
    state.lastContractHash = '';
    updateStats(0, 0);
    setStatus('Mapa limpio', 'Se ha vaciado el render actual.', 'ok');
    updateCacheBadge([]);
  }

  function clearMarkers() {
    if (!state.map) {
      state.markers = [];
      return;
    }
    for (const marker of state.markers) {
      state.map.markers.remove(marker);
    }
    state.markers = [];
  }

  function updateStats(routeCount, stopCount) {
    if (ui.routesCount) {
      ui.routesCount.textContent = String(routeCount);
    }
    if (ui.stopsCount) {
      ui.stopsCount.textContent = String(stopCount);
    }
  }

  function updateCacheBadge(routeGroups) {
    const withPath = routeGroups.filter(function (item) { return item.pathPoints.length > 1; }).length;
    ui.cacheBadge.textContent = `${withPath}/${routeGroups.length} rutas con trazado`;
  }

  function setStatus(title, message, tone) {
    ui.statusBox.dataset.tone = tone || 'info';
    ui.statusBox.querySelector('strong').textContent = title;
    ui.statusText.textContent = message;
  }

  function getPadding() {
    return DEFAULT_PADDING;
  }

  function getShapePositions(shape) {
    if (!shape) {
      return [];
    }

    if (typeof shape.getCoordinates === 'function') {
      return flattenCoordinates(shape.getCoordinates());
    }

    if (typeof shape.getGeometry === 'function') {
      return getShapePositions(shape.getGeometry());
    }

    if (shape.geometry) {
      return getShapePositions(shape.geometry);
    }

    if (Array.isArray(shape.coordinates)) {
      return flattenCoordinates(shape.coordinates);
    }

    return [];
  }

  function flattenCoordinates(coordinates) {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return [];
    }

    if (
      coordinates.length >= 2 &&
      Number.isFinite(Number(coordinates[0])) &&
      Number.isFinite(Number(coordinates[1]))
    ) {
      return [[Number(coordinates[0]), Number(coordinates[1])]];
    }

    return coordinates.reduce(function (accumulator, item) {
      return accumulator.concat(flattenCoordinates(item));
    }, []);
  }

  function rememberInitialViewSnapshot(positions, fitMaxZoom) {
    if (!state.map || !positions.length) {
      return;
    }

    window.setTimeout(function () {
      if (!state.map) {
        return;
      }

      const effectiveFitMaxZoom = fitMaxZoom != null ? clampZoom(fitMaxZoom) : MAP_MAX_ZOOM;
      state.initialViewSnapshot = {
        positions: positions.map(function (position) { return [position[0], position[1]]; }),
        routeIds: Array.from(state.activeRouteIds),
        fitMaxZoom: effectiveFitMaxZoom,
      };
    }, 50);
  }

  function toPositiveInt(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
  }

  async function setContract(contract) {
    await renderContract(contract || {});
  }

  function resize() {
    if (!state.map) {
      return;
    }

    state.map.resize();
    if (ui.autoFit.checked && state.boundsSource && state.boundsSource.getShapes().length > 0) {
      fitToRenderedData(false, state.lastRenderFitMaxZoom);
    }
  }

  function destroy() {
    clearTimeout(state.resizeTimer);
    for (const binding of eventBindings) {
      if (binding && binding.target && typeof binding.target.removeEventListener === 'function') {
        binding.target.removeEventListener(binding.eventName, binding.handler);
      }
    }
    eventBindings.length = 0;
    destroyMap();
  }

  function zoomIn() {
    stepZoom(1);
  }

  function zoomOut() {
    stepZoom(-1);
  }

  function resetView() {
    resetMapViewToInitialFit();
  }

  function setStyle(style) {
    if (state.map && style) {
      state.map.setStyle({ style });
    }
  }

  init();

  return {
    destroy,
    resize,
    setContract,
    setTrafficEnabled,
    setVisibleRouteIds,
    renderFromInput,
    clearMap,
    zoomIn,
    zoomOut,
    resetView,
    setStyle,
  };
}
